/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  UI_OPERATION_STATUS_REQUEST,
} from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  FormCheckbox as Checkbox,
  FormEmail as EmailField,
  FormTextField as TextField,
} from '@uncinc/uncinc-react-kitchen-sink';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';

import { Button, PasswordField } from '../../../components';
import {
  MINIMUM_PASSWORD_CHARACTER_COUNT,
  ENABLE_AIDA_SCREENING,
  DOMAIN_ZELFHULP_ALCOHOL,
  ENABLE_BUDDY_CO_SCREENING,
  DOMAIN_ZELFHULP_GOKKEN,
} from '../../../config/constants';
import getCurrentDomain from '../../../helpers/getCurrentDomain';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import useOperationStatus from '../../../hooks/useOperationStatus';

// Temporary. See file for more information.
import { userRegisterWithPassword } from '../../../store/actions/Login';
import useDispatch from '../../../hooks/useTypedDispatch';

const OnboardingForm: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { privacy, joyride, onboarding } = useRoutes();
  const { buildClass } = useClasses('PageOnboarding__form');
  const history = useHistory();

  const isLoggedIn = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['login', 'loggedIn'], null));

  React.useEffect(() => {
    if (isLoggedIn) {
      if (ENABLE_AIDA_SCREENING && getCurrentDomain() === DOMAIN_ZELFHULP_ALCOHOL) {
        history.push(onboarding.aida);
      } else if (ENABLE_BUDDY_CO_SCREENING && getCurrentDomain() === DOMAIN_ZELFHULP_GOKKEN) {
        history.push(onboarding.buddyCo);
      } else {
        history.push(joyride);
      }
    }
  }, [history, isLoggedIn, joyride, onboarding.aida, onboarding.buddyCo]);

  const registerOperationStatus = useOperationStatus('user', 'register');

  const handleSubmit = ({ fullName, email, password }) => {
    dispatch(userRegisterWithPassword(email, password, fullName));
  };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .trim()
      .required(t('pages.onboarding_credentials_default.fields.fullName.validation.required')),
    email: Yup.string()
      .email(t('pages.onboarding_credentials_default.fields.email.validation.email'))
      .required(t('pages.onboarding_credentials_default.fields.email.validation.required')),
    password: Yup.string()
      .min(MINIMUM_PASSWORD_CHARACTER_COUNT, t('pages.reset_password.fields.password.validation.min', { count: MINIMUM_PASSWORD_CHARACTER_COUNT }))
      .matches(/^(?=.*\d)(?=.*[a-zA-Z]).*$/, t('pages.reset_password.fields.password.validation.invalid'))
      .required(t('pages.onboarding_credentials_default.fields.password.validation.required')),
    privacy: Yup.boolean()
      .isTrue(t('pages.onboarding_credentials_default.fields.privacy.validation.required'))
      .required(t('pages.onboarding_credentials_default.fields.privacy.validation.required')),
  });

  return (
    <FormikWrapper
      initialValues={{
        fullName: '',
        email: '',
        password: '',
        privacy: false,
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      formProps={{ noValidate: true }}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values);
        setSubmitting(false);
      }}
    >
      <TextField
        fieldName="fullName"
        title={t('pages.onboarding_credentials_default.fields.fullName.label')}
        description={t('pages.onboarding_credentials_default.fields.fullName.description')}
        placeholder={t('pages.onboarding_credentials_default.fields.fullName.placeholder')}
      />
      <EmailField
        fieldName="email"
        title={t('pages.onboarding_credentials_default.fields.email.label')}
        placeholder={t('pages.onboarding_credentials_default.fields.email.placeholder')}
      />
      <PasswordField
        fieldName="password"
        title={t('pages.onboarding_credentials_default.fields.password.label')}
        description={t('pages.onboarding_credentials_default.fields.password.description')}
      />
      <Checkbox
        fieldName="privacy"
        title={(
          <Trans i18nKey="pages.onboarding_credentials_default.fields.privacy.label">
            {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
            <a href={privacy} target="_blank" rel="noreferrer" className="textual-link" />
          </Trans>
        )}
      />
      <div className={buildClass('buttons')}>
        <Button
          className={buildClass('submit')}
          htmlType="submit"
          buttonState={registerOperationStatus === UI_OPERATION_STATUS_REQUEST ? 'disabled' : undefined}
        >
          {t('pages.onboarding_credentials_default.submit')}
        </Button>
      </div>
    </FormikWrapper>
  );
};

export default OnboardingForm;
